/**
 * Editor AI agent contract shared by the server and the browser.
 *
 * The package never calls an AI provider itself. Each consuming app connects
 * its own AI settings by implementing {@link DocumentAgentAiAdapter} on the
 * server, and its own persistence/permission checks around it.
 */

/** Co-author edits appear as tracked suggestions; Auto-update applies straight away with a brief highlight. */
export const DOCUMENT_AGENT_MODES = ['coauthor', 'auto'] as const;
export type DocumentAgentMode = (typeof DOCUMENT_AGENT_MODES)[number];
export const DEFAULT_DOCUMENT_AGENT_MODE: DocumentAgentMode = 'coauthor';

export const DOCUMENT_AGENT_MODE_COPY: Record<DocumentAgentMode, { label: string; description: string }> = {
  coauthor: {
    label: 'Co-author',
    description: 'Works in the live document like another person. Its changes appear as tracked suggestions everyone can accept or reject.',
  },
  auto: {
    label: 'Auto-update',
    description: 'Makes changes straight away. Changed content is highlighted for a moment so you can read it and cancel.',
  },
};

/** Browser storage key for a person's chosen mode. Missing or invalid values fall back to Co-author. */
export function documentAgentModeStorageKey(userId: string): string {
  return 'document-agent-mode:' + userId;
}

export function parseDocumentAgentMode(value: unknown): DocumentAgentMode {
  return value === 'auto' ? 'auto' : DEFAULT_DOCUMENT_AGENT_MODE;
}

/** Hard bounds for every agent request and response. Servers enforce them; browsers mirror them for feedback. */
export const DOCUMENT_AGENT_LIMITS = {
  messageChars: 4000,
  titleChars: 200,
  documentChars: 100_000,
  historyChars: 20_000,
  historyTurns: 5,
  answerChars: 100_000,
  responseBytes: 180_000,
  timeoutMs: 90_000,
  targetSections: 20,
} as const;

/** Highlight window for agent-changed text, and the Co-author auto-accept delay. */
export const DOCUMENT_AGENT_FLASH_MS = 2000;
export const DOCUMENT_AGENT_SUGGESTION_AUTO_ACCEPT_MS = 5000;

export const DOCUMENT_AGENT_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
export type DocumentAgentImageType = (typeof DOCUMENT_AGENT_IMAGE_TYPES)[number];

/** Returns the user-facing problem with an image the person picked for the agent, or null when it is acceptable. */
export function documentAgentImageProblem(file: { type: string; size: number }, maxBytes: number): string | null {
  if (!(DOCUMENT_AGENT_IMAGE_TYPES as readonly string[]).includes(file.type)) return 'Choose a PNG, JPEG, GIF or WebP image.';
  if (file.size <= 0 || file.size > maxBytes) return 'Images must be ' + formatImageLimit(maxBytes) + ' or smaller.';
  return null;
}

function formatImageLimit(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return (Number.isInteger(mb) ? String(mb) : mb.toFixed(1)) + ' MB';
}

/**
 * Keeps the newest completed turns that fit the history budget and returns them oldest first.
 * `newestFirst` must already be ordered newest to oldest.
 */
export function boundDocumentAgentHistory<T extends { message: string; answer: string | null }>(
  newestFirst: readonly T[],
  budgetChars: number = DOCUMENT_AGENT_LIMITS.historyChars,
): T[] {
  let remaining = budgetChars;
  const kept: T[] = [];
  for (const row of newestFirst.slice(0, DOCUMENT_AGENT_LIMITS.historyTurns)) {
    const size = row.message.length + (row.answer?.length ?? 0);
    if (size > remaining) continue;
    remaining -= size;
    kept.push(row);
  }
  return kept.reverse();
}

export interface DocumentAgentAiMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface DocumentAgentAiRequest {
  /** Stable request identity for provider tracing and idempotency. */
  requestId: string;
  messages: readonly DocumentAgentAiMessage[];
  maxResponseBytes: number;
  timeoutMs: number;
  signal: AbortSignal;
  /** Receives each streamed text delta. Throwing stops the request. */
  onDelta: (delta: string) => Promise<void>;
}

/** Server-side AI connection supplied by the consuming app. Returns the complete response text. */
export interface DocumentAgentAiAdapter {
  complete(request: DocumentAgentAiRequest): Promise<string>;
}
